import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import ClientApp from 'purecloud-client-app-sdk';
import { wizardConfig, getDataRegion, sanitizeOrgName, INSIGHTS_URL } from './config/wizard-config';
import {
  authenticateGenesysCloud,
  tryRestoreGenesysSession,
  getCurrentUser,
  getOrganizationMe,
  validateProductAvailability,
  isEmbeddedInIframe,
  findExistingOAuthClients,
  checkGenesysTokenAlive,
  clearGenesysToken,
} from './services/genesys-api';
import {
  GenesysUnavailableError,
  isSessionExpired,
  reportSessionExpired,
  resetSessionExpired,
  useSessionExpired,
  watchSession,
} from './services/session';
import { usableAccessToken } from './services/token-store';
import { signInFailureKind } from './services/auth-popup-guard';
import {
  beginResume,
  discardResumeSnapshot,
  endResume,
  saveResumeSnapshot,
  type ResumePage,
} from './services/reauth-resume';
import { useI18n } from './i18n/I18nProvider';
import { englishCatalog, loadCatalog } from './i18n/catalog';
import { readLaunchLangTag } from './i18n/feature';
import { languageForScreen } from './i18n/resolve';
import { applyDocumentLanguage, type LanguageCode, type MessageCatalog } from './i18n/types';
import { syncActiveLanguage, t, tEnglish } from './services/language-manager';
import {
  install,
  uninstall,
  checkExisting,
  forgetLeftoverClient,
  readLeftoverClient,
  removeLeftoverClient,
  type LeftoverOAuthClient,
  type ProvisioningProgress as ProgressType,
  type InstallMode,
  type ExistingOrgStatus,
} from './services/wizard';
import {
  DEV_PREVIEW_ENABLED,
  isFreshFromUrlQuery,
  isFullyProvisioned,
  isInstallChromePage,
  isInstallUiPage,
  landingDestinations,
  readDevWizardPreview,
  resolvePostAuthRoute,
  restoreLaunchParamsFromOAuthState,
} from './install-route';

import { LoadingSpinner } from './components/LoadingSpinner';
import { StepIndicator } from './components/StepIndicator';
import { ErrorPage } from './components/ErrorPage';
import { SignInPage } from './components/SignInPage';
import { WelcomePage } from './components/WelcomePage';
import { SetupPage } from './components/SetupPage';
import { InstallSummaryPage } from './components/InstallSummaryPage';
import { ProvisioningProgress } from './components/ProvisioningProgress';
import { SetupCompletePage } from './components/SetupCompletePage';
import { LandingPage } from './components/LandingPage';
import { SettingsPage } from './components/SettingsPage';
import { ConversationActionsPage } from './components/ConversationActionsPage';
import { ConfirmDialogHost, confirmDialog } from './components/ConfirmDialog';
import { SessionExpiredDialog } from './components/SessionExpiredDialog';
import logo from './assets/mediabriz-logo-svg-400px.svg';

type Page =
  | 'loading'
  | 'signin'
  | 'welcome'
  | 'setup'
  | 'install-summary'
  | 'provisioning'
  | 'complete'
  | 'landing'
  | 'settings'
  | 'actions'
  | 'error'
  | 'uninstalling';

type SettingsReturnPage = 'landing' | 'complete';

const WIZARD_STEPS = ['Welcome', 'Configuration', 'Summary', 'Complete'];

/** Error page title and message for a failed sign-in; the message also shows in the session-expired dialog. */
const SIGN_IN_FAILURE_KEYS: Record<ReturnType<typeof signInFailureKind>, { title: string; message: string }> = {
  closed: { title: 'error-popup-closed', message: 'error-popup-closed-msg' },
  popup: { title: 'error-popup-blocked', message: 'error-popup-blocked-msg' },
  other: { title: 'error-access-invalid', message: 'error-access-invalid-msg' },
};

function getQueryParams(): Record<string, string> {
  const params: Record<string, string> = {};
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.forEach((value, key) => {
    params[key] = value;
  });

  // Also parse from hash (OAuth redirect)
  if (window.location.hash) {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    hashParams.forEach((value, key) => {
      if (!params[key]) params[key] = value;
    });
  }

  return params;
}

// OAuth Authorization Code Grant response artifacts. They live on the wizard's URL after the
// PKCE round-trip back from Genesys's login host (and get restored into appParams via the
// `state` round-trip in the setup() effect). Forwarding them to insights would pollute the
// insights URL with the wizard's auth response, which insights neither consumes nor needs —
// insights runs its OWN PKCE flow against the shared OAuth client. Strip them before forwarding.
const OAUTH_RESPONSE_PARAMS = new Set([
  'code', 'state', 'error', 'error_description', 'errorCode', 'errorDescription', 'session_state',
]);

function userHasPermission(userPerms: string[], required: string): boolean {
  return userPerms.some((up) => up === required || up.startsWith(required));
}

/** Settings, Conversation Actions, and Login Limit. CloudBriz Admin. */
function canManageWizardSettings(userPerms: string[]): boolean {
  return userHasPermission(userPerms, wizardConfig.premiumAppAdminPermission);
}

/** Wizard-only query keys — never forward to insights. */
const INSIGHTS_FORWARD_SKIP = new Set([
  'fresh', 'uninstall', 'preview', 'scanEnabled', 'aiActionsEnabled', 'loginLimit', 'conversations',
  'lang', 'dialog', 'previewError', 'loading', 'llState', 'llReport',
]);

/**
 * Build the Mediabriz Insights redirect URL with all Genesys-issued launch params forwarded.
 * Used by the Landing Page Insights CTA (`window.open` named `mediabriz-insights`).
 * Insights's `acquireGenesysContext` consumes these to resolve the regional Genesys
 * env via `purecloud-client-app-sdk`'s `ClientApp`. See `docs/INSIGHTS_DASHBOARD_WORKPLAN.md`
 * §7.1 ("Option A.1 handoff design") + §8.2 ("Token acquisition").
 */
function buildInsightsUrl(appParams: Record<string, string>): string {
  const url = new URL(INSIGHTS_URL);
  for (const [key, value] of Object.entries(appParams)) {
    if (!OAUTH_RESPONSE_PARAMS.has(key) && !INSIGHTS_FORWARD_SKIP.has(key)) {
      url.searchParams.set(key, value);
    }
  }
  return url.toString();
}

export function App() {
  const i18n = useI18n();
  const [page, setPage] = useState<Page>('loading');
  const [loadingKey, setLoadingKey] = useState('loading');
  const [currentStep, setCurrentStep] = useState(1);
  const [gcEnvironment, setGcEnvironment] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgShortName, setOrgShortName] = useState('');
  const [orgId, setOrgId] = useState('');
  const [dataRegion, setDataRegion] = useState('US');
  const [gcLangTag, setGcLangTag] = useState(readLaunchLangTag);
  const [gcTargetEnv, setGcTargetEnv] = useState('');
  // Pre-computed Mediabriz Insights redirect URL with all Genesys launch params forwarded.
  // Built once during setup() (when appParams is still in scope) so the Landing Insights
  // CTA doesn't have to re-derive it from `window.location.search` at click time (by which
  // point OAuth round-trip artifacts have replaced the original Genesys params).
  const [insightsUrl, setInsightsUrl] = useState('');
  const [isInstalling, setIsInstalling] = useState(false);
  const [provisioningSteps, setProvisioningSteps] = useState<ProgressType[]>([]);
  const [installMode, setInstallMode] = useState<InstallMode>('create');
  const [existingOrgInfo, setExistingOrgInfo] = useState<{ organization: string; region: string } | undefined>();
  // Landing destinations (beyond Insights): Settings for every CloudBriz Admin
  // (reply translation and other conversations need no org flag); Conversation
  // Actions, its own page, also needs `aiActionsEnabled`. See landingDestinations().
  const [showSettingsRow, setShowSettingsRow] = useState(false);
  const [loginLimitEnabled, setLoginLimitEnabled] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  // Operator flag (missing = on). Off: a Login Limit only org, with no
  // conversation pages, settings or Setup Complete steps.
  const [conversationsEnabled, setConversationsEnabled] = useState(true);
  // Landing tells a user without CloudBriz Admin why Settings or Conversation Actions is missing.
  const [showAdminNote, setShowAdminNote] = useState(false);
  const [settingsReturnPage, setSettingsReturnPage] = useState<SettingsReturnPage>('landing');
  // Tracked so Setup Complete / Landing can show the Login Limit tab and gate
  // Conversation Actions after credential-update installs (create writes
  // aiActionsEnabled: true; update does not touch the flags).
  const [orgLoginLimitEnabled, setOrgLoginLimitEnabled] = useState(false);
  const [orgAiActionsEnabled, setOrgAiActionsEnabled] = useState(false);
  // Uninstall confirm is English even though it opens over the loading screen.
  const [uninstallConfirm, setUninstallConfirm] = useState(false);

  const preview = DEV_PREVIEW_ENABLED ? readDevWizardPreview() : null;
  const displayLanguage = languageForScreen({
    page,
    languagesEnabled: i18n.languagesEnabled,
    stored: i18n.manual,
    langTag: gcLangTag || null,
    previewLang: preview?.lang ?? null,
    forceEnglish: uninstallConfirm,
  }).language;
  const [screenCatalog, setScreenCatalog] = useState<{ code: LanguageCode; catalog: MessageCatalog }>({
    code: 'en',
    catalog: englishCatalog,
  });
  useEffect(() => {
    let cancelled = false;
    void loadCatalog(displayLanguage).then((loaded) => {
      if (!cancelled) setScreenCatalog({ code: displayLanguage, catalog: loaded });
    });
    return () => {
      cancelled = true;
    };
  }, [displayLanguage]);
  // Until the new catalog arrives, keep showing the loaded one with its own
  // direction, so Hebrew text is never laid out left to right (or the reverse).
  const shownLanguage: LanguageCode = displayLanguage === 'en' ? 'en' : screenCatalog.code;
  syncActiveLanguage(shownLanguage, shownLanguage === 'en' ? englishCatalog : screenCatalog.catalog);
  useLayoutEffect(() => {
    applyDocumentLanguage(shownLanguage);
  }, [shownLanguage]);

  // Error state. Keys, translated while the error screen is showing.
  const [errorTitleKey, setErrorTitleKey] = useState('error-title');
  const [errorMessageKey, setErrorMessageKey] = useState('error-service-unavailable-msg');
  const [errorDetails, setErrorDetails] = useState<string | undefined>();
  const [missingPermissions, setMissingPermissions] = useState<string[] | undefined>();
  const [isServiceUnavailable, setIsServiceUnavailable] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  // Embedded Apps iframe: finishSetup runs only after the user clicks Sign in
  // so window.open for Genesys login stays inside a user gesture.
  const pendingFinishSetupRef = useRef<(() => Promise<void>) | null>(null);
  // Install UI is allowed only after resolvePostAuthRoute proves the org is
  // not fully provisioned. The goToPage guard refuses Welcome / setup / etc.
  const installEligibleRef = useRef(false);
  // Sign in again after the session expired re-runs the same post-auth setup.
  const finishSetupRef = useRef<(() => Promise<void>) | null>(null);
  const [signedIn, setSignedIn] = useState(false);
  const sessionExpired = useSessionExpired();
  const [reauthBusy, setReauthBusy] = useState(false);
  const [reauthErrorKey, setReauthErrorKey] = useState<string | null>(null);
  // The page reopened after that sign-in; its restored edits are dropped once the admin leaves it.
  const resumedPageRef = useRef<Page | null>(null);

  const showError = useCallback((titleKey: string, messageKey: string, details?: string, permissions?: string[], serviceUnavailable?: boolean) => {
    setErrorTitleKey(titleKey);
    setErrorMessageKey(messageKey);
    setErrorDetails(details);
    setMissingPermissions(permissions);
    setIsServiceUnavailable(serviceUnavailable || false);
    setPage('error');
  }, []);

  const goToPage = useCallback((next: Page) => {
    if (isInstallUiPage(next) && !installEligibleRef.current) {
      console.error('[WIZARD] Blocked install page; org is not eligible for install', { page: next });
      showError(
        'error-service-unavailable',
        'error-install-not-eligible',
        undefined, undefined, true,
      );
      return;
    }
    setPage(next);
  }, [showError]);

  const showSignInFailure = useCallback((e: unknown) => {
    const msg = e instanceof Error ? e.message : String(e);
    const keys = SIGN_IN_FAILURE_KEYS[signInFailureKind(e)];
    showError(keys.title, keys.message, msg);
  }, [showError]);

  /** Error screen for an OAuth client a failed install could not remove. */
  const showLeftoverClient = useCallback((client: LeftoverOAuthClient, cause?: string) => {
    const line = t('error-install-cleanup-client', { name: client.name, id: client.id });
    showError(
      'error-install-cleanup-title',
      isSessionExpired() ? 'error-install-cleanup-pending-msg' : 'error-install-cleanup-msg',
      cause ? `${line}\n${cause}` : line,
    );
  }, [showError]);

  // Initialization
  useEffect(() => {
    async function setup() {
      try {
        const appParams = getQueryParams();

        // Dev-only standalone preview of Setup Complete / Landing. Compiled out of
        // prod (`--mode prod`). No Genesys login — operator can paste the CloudFront URL.
        if (DEV_PREVIEW_ENABLED) {
          const preview = readDevWizardPreview();
          if (preview) {
            console.info(`[WIZARD] langTag ${appParams.langTag ? 'present' : 'absent'}`);
            setInsightsUrl(buildInsightsUrl(appParams));
            setGcEnvironment(wizardConfig.defaultGcEnvironment);
            setDataRegion('US');
            setOrgName('Acme');
            setOrgShortName('acme');
            setOrgId('preview-org');
            setConversationsEnabled(preview.conversations);
            if (preview.page === 'landing') {
              setShowSettingsRow(true);
              setLoginLimitEnabled(preview.loginLimit);
              setShowActionsMenu(preview.aiActionsEnabled && preview.conversations);
            } else {
              // `?preview=settings` includes Login Limit. `loginLimit=0` opens the
              // page without that section, so the tab bar stays hidden.
              const loginLimitQuery = new URLSearchParams(window.location.search).get('loginLimit');
              const loginLimitPreviewOff = loginLimitQuery === '0' || loginLimitQuery === 'false';
              setShowSettingsRow(true);
              setLoginLimitEnabled(loginLimitPreviewOff ? false : preview.loginLimit || preview.page === 'settings');
              setShowActionsMenu(preview.conversations && (preview.aiActionsEnabled || preview.page === 'actions'));
            }
            setCurrentStep(preview.page === 'welcome' ? 1 : preview.page === 'setup' ? 2 : preview.page === 'install-summary' ? 3 : 4);
            setProvisioningSteps([
              { step: 'oauth-client', status: 'completed' },
              { step: 'register-org', status: 'in-progress' },
            ]);
            if (preview.page === 'loading') setLoadingKey(preview.loadingKey);
            if (preview.page === 'uninstalling') setLoadingKey('loading-uninstalling');
            if (preview.page === 'error') {
              if (preview.errorKind === 'permissions') {
                showError('error-missing-permissions-title', 'error-missing-permissions', undefined, [
                  'integration:cloudBriz:admin',
                  'oauth:client:view',
                ]);
              } else if (preview.errorKind === 'unavailable') {
                showError('error-service-unavailable', 'error-service-unavailable-msg', undefined, undefined, true);
              } else {
                showError('error-installation', 'error-post-custom-setup-msg', 'Save failed (HTTP 500)');
              }
            } else {
              setPage(preview.page);
            }
            if (preview.dialog && (preview.page === 'actions' || preview.page === 'settings' || preview.page === 'landing')) {
              void confirmDialog({
                title: t('settings-actions-delete-confirm-title'),
                subject: 'Create order',
                message: t('settings-actions-delete-confirm'),
                confirmLabel: t('settings-actions-delete'),
                cancelLabel: t('confirm-dialog-cancel'),
                danger: true,
              });
            }
            return;
          }
        }

        // On OAuth redirect-back (Authorization Code Grant with PKCE), Genesys redirects
        // to the registered redirect URI with the OAuth response in the URL query string
        // but WITHOUT the original launch query string. That means region launch params
        // (`environment` for the dev/standalone path; `hostOrigin` + `targetEnv` for the
        // AppFoundry iframe path) and `langTag` are lost on the second load and the wizard
        // would silently fall back to the default region. The wizard round-trips the
        // launch params through the OAuth `state` blob in authenticateGenesysCloud();
        // restore them here BEFORE resolving the env so every region behaves identically
        // and the post-OAuth load reuses the same context as the initial load. URL-explicit
        // values still win over restored values. `fresh` is never copied from state.
        if (appParams.state) {
          try {
            restoreLaunchParamsFromOAuthState(appParams, appParams.state);
          } catch {
            // state was not our JSON payload (e.g. opaque value from another flow); ignore
          }
        }

        // Resolve Genesys Cloud environment
        let env = wizardConfig.defaultGcEnvironment;
        let hostOrigin = '';
        let targetEnv = '';

        if (appParams.hostOrigin && appParams.targetEnv) {
          try {
            hostOrigin = appParams.hostOrigin;
            targetEnv = appParams.targetEnv;
            const clientApp = new ClientApp({
              gcHostOrigin: hostOrigin,
              gcTargetEnv: targetEnv,
            });
            env = clientApp.gcEnvironment;
          } catch {
            env = appParams.environment || wizardConfig.defaultGcEnvironment;
            hostOrigin = 'https://apps.' + env;
            targetEnv = 'default';
          }
        } else {
          env = appParams.environment || wizardConfig.defaultGcEnvironment;
          hostOrigin = 'https://apps.' + env;
          targetEnv = 'default';
        }

        setGcEnvironment(env);

        // Store Genesys context params for backend registration
        if (appParams.langTag) {
          setGcLangTag(appParams.langTag);
          i18n.reportLangTag(appParams.langTag);
        }
        console.info(`[WIZARD] langTag ${appParams.langTag ? 'present' : 'absent'}`);
        if (appParams.targetEnv) setGcTargetEnv(appParams.targetEnv);

        // Pre-compute the insights redirect URL once, while appParams still holds the original
        // Genesys launch params (after the `state`-blob restore above). Used by the Landing
        // Page Insights CTA.
        setInsightsUrl(buildInsightsUrl(appParams));

        const lang = appParams.langTag || appParams[wizardConfig.languageQueryParam] || wizardConfig.defaultLanguage;

        // Check for OAuth error in redirect
        if (appParams.error) {
          if (appParams.error_description || appParams.errorDescription) {
            const desc = appParams.error_description || appParams.errorDescription || '';
            if (appParams.errorCode === 'access_denied' || appParams.error === 'access_denied') {
              showError('error-access-denied', 'error-access-denied-msg', desc);
            } else {
              showError('error-access-invalid', 'error-access-invalid-msg', desc);
            }
            return;
          }
        }

        // Auth + post-auth setup. Defined here so embedded mode can run it only
        // after a Sign in click (browsers block async window.open without a gesture).
        const finishSetup = async () => {
          installEligibleRef.current = false;
          setPage('loading');
          setLoadingKey('loading-authenticating');
          // Do not round-trip `fresh` through the OAuth state blob. After uninstall
          // it lives on the URL query only (`?fresh=true`); restoring it from state
          // would skip the already-installed check on a later Apps → MediaBriz open.
          const launchParams = { ...appParams };
          delete launchParams.fresh;
          const state = await authenticateGenesysCloud(env, {
            ...launchParams,
            hostOrigin,
            targetEnv,
          });
          resetSessionExpired();
          setReauthErrorKey(null);
          setSignedIn(true);

          // Get current user and organization details
          const userMe = await getCurrentUser();
          const userOrgName = userMe.organization?.name || '';
          const userOrgId = userMe.organization?.id || '';
          setOrgName(userOrgName);
          setOrgId(userOrgId);

          // Get org short name from the Organization API (domain field)
          let userOrgShortName = '';
          try {
            const orgDetails = await getOrganizationMe();
            const details = orgDetails as Record<string, unknown>;
            userOrgShortName = (details.domain as string) || '';
            setOrgShortName(userOrgShortName);
          } catch (e) {
            console.warn('Could not retrieve organization short name:', e);
          }

          // Auto-detect data region
          setDataRegion(getDataRegion(env));

          // Check for uninstall
          if (wizardConfig.enableUninstall && (state.uninstall === 'true' || appParams.uninstall === 'true')) {
            flushSync(() => setUninstallConfirm(true));
            const confirmed = await confirmDialog({
              title: tEnglish('uninstall-confirm-title'),
              message: tEnglish('uninstall-confirm'),
              confirmLabel: tEnglish('uninstall-confirm-button'),
              cancelLabel: tEnglish('confirm-dialog-cancel'),
              danger: true,
              dir: 'ltr',
            });
            // Uninstalling is English by its screen rule. A failed uninstall's
            // error screen follows the chosen language.
            setUninstallConfirm(false);
            if (confirmed) {
              setPage('uninstalling');
              setLoadingKey('loading-uninstalling');
              try {
                await uninstall(userOrgId, env);
                setLoadingKey('uninstall-complete');
                setTimeout(() => {
                  window.location.href = `${wizardConfig.wizardUriBase}index.html?langTag=${encodeURIComponent(lang)}&hostOrigin=${encodeURIComponent(hostOrigin)}&targetEnv=${encodeURIComponent(targetEnv)}&fresh=true`;
                }, 2000);
              } catch (e: unknown) {
                let msg: string;
                if (e instanceof Error) {
                  msg = `${e.name}: ${e.message}`;
                } else if (typeof e === 'object' && e !== null) {
                  const errObj = e as Record<string, unknown>;
                  msg = errObj.message as string || errObj.body as string || JSON.stringify(e);
                } else {
                  msg = String(e);
                }
                showError('error-installation', 'uninstall-title', msg);
              }
            } else {
              // User cancelled uninstall -- redirect back to wizard in fresh state
              window.location.href = `${wizardConfig.wizardUriBase}index.html?langTag=${encodeURIComponent(lang)}&hostOrigin=${encodeURIComponent(hostOrigin)}&targetEnv=${encodeURIComponent(targetEnv)}`;
            }
            return;
          }

          // Check existing installation via MediaBriz backend organization table (by Genesys
          // org ID). This runs BEFORE the product-availability check (moved below) on
          // purpose: /api/check-org only needs the user's Genesys token (no admin
          // permission), so an already-provisioned org reaches the Insights launcher
          // without CloudBriz Admin. A View-only user can open Insights. Settings
          // and Conversation Actions require CloudBriz Admin.
          //
          // Always run the check. `fresh=true` is URL-query-only (after uninstall) and
          // must never skip this: a finished org that inherited fresh from OAuth state
          // would otherwise land on Welcome. Fail closed on check errors.
          const freshFromUrl = isFreshFromUrlQuery();
          setLoadingKey('loading-checking');
          let existingStatus: ExistingOrgStatus;
          try {
            existingStatus = await checkExisting(userOrgId, env);
          } catch (e) {
            console.error('[WIZARD] checkExisting failed', {
              orgId: userOrgId,
              region: env,
              freshFromUrl,
              skipExistingCheck: false,
              error: e,
            });
            showError(
              'error-service-unavailable',
              e instanceof GenesysUnavailableError ? 'error-genesys-unavailable' : 'error-service-unavailable-msg',
              undefined, undefined, true,
            );
            return;
          }

          // An install in this tab failed and could not remove its OAuth client (often
          // because the session had expired). Remove it now, unless the org finished
          // installing meanwhile: its registration may use that client.
          const leftover = readLeftoverClient(userOrgId);
          if (leftover) {
            if (isFullyProvisioned(existingStatus)) {
              forgetLeftoverClient();
            } else if (!(await removeLeftoverClient(leftover))) {
              showLeftoverClient(leftover);
              return;
            }
          }

          let oauthClientCount: number | null = null;
          if (!isFullyProvisioned(existingStatus)) {
            try {
              const clients = await findExistingOAuthClients();
              oauthClientCount = clients.length;
            } catch (e) {
              console.warn('[WIZARD] findExistingOAuthClients failed', {
                orgId: userOrgId,
                region: env,
                error: e,
              });
              oauthClientCount = existingStatus.exists ? 0 : null;
            }
          }

          const route = resolvePostAuthRoute(existingStatus, oauthClientCount);
          console.info('[WIZARD] Post-auth route', {
            orgId: userOrgId,
            region: env,
            exists: existingStatus.exists,
            hasCredentials: existingStatus.hasCredentials,
            organization: existingStatus.organization,
            freshFromUrl,
            skipExistingCheck: false,
            oauthClientCount,
            route: route.kind,
            routeReason: route.kind === 'landing' || route.kind === 'error' ? route.reason : undefined,
          });

          if (route.kind === 'error') {
            showError(
              'error-service-unavailable',
              'error-service-unavailable-msg',
              undefined, undefined, true,
            );
            return;
          }

          if (route.kind === 'landing') {
            // Already-provisioned orgs skip the install flow (and the product-availability check) on re-entry. The v1.3.0 initial
            // design did an immediate `window.location.replace(insightsUrl)` here (per workplan
            // §7.1 "Option A.1"), but live testing surfaced two real UX gaps with the
            // flash-redirect: (a) no Mediabriz-branded "you've reached Mediabriz" surface
            // inside the Genesys iframe — users read the cross-domain flash as a glitch, and
            // (b) zero support discoverability for users who hit a problem opening insights
            // (auth issue, missing permission, firewall block, etc.) since they're punted to
            // a different origin before they can ask for help. The landing page closes both
            // gaps by rendering a Mediabriz-branded hub (Insights always; Settings
            // and Conversation Actions when permitted) plus a Support-only footer.
            // See `frontend/src/components/LandingPage.tsx` for the CTA mechanics
            // (named window.open target so repeated clicks re-focus the existing
            // insights tab instead of opening duplicates).
            //
            // Insights is offered to everyone who reaches Landing. The dashboard
            // loads for CloudBriz View or CloudBriz Admin. Settings and
            // Conversation Actions require CloudBriz Admin plus the matching
            // org flag, so View-only users stay Insights-only and see a note
            // that CloudBriz Admin is required.
            //
            // Settings is shown to every CloudBriz Admin, whatever the org flags,
            // except a Login Limit only org without Login Limit (nothing to set).
            // The Guard block inside the page still renders only when scanEnabled is true.
            // See docs/INSTALL_FLOW.md "Settings button vs scanEnabled".
            const userPerms: string[] = userMe.authorization?.permissions || [];
            const loginLimitOn = existingStatus.loginLimitEnabled === true;
            const aiActionsOn = existingStatus.aiActionsEnabled !== false;
            const conversationsOn = existingStatus.conversationsEnabled !== false;
            const canManageSettings = canManageWizardSettings(userPerms);
            const destinations = landingDestinations({
              canManageSettings,
              loginLimitEnabled: loginLimitOn,
              aiActionsEnabled: aiActionsOn,
              conversationsEnabled: conversationsOn,
            });
            setOrgLoginLimitEnabled(loginLimitOn);
            setLoginLimitEnabled(loginLimitOn);
            setConversationsEnabled(conversationsOn);
            setShowSettingsRow(destinations.settings);
            setShowActionsMenu(destinations.actions);
            setShowAdminNote(!canManageSettings);
            setCurrentStep(4);
            // After Sign in again, reopen the page the session expired on, if still allowed.
            const resumePage = beginResume(userOrgId);
            if (resumePage === 'settings' && destinations.settings) {
              resumedPageRef.current = 'settings';
              setSettingsReturnPage('landing');
              goToPage('settings');
              return;
            }
            if (resumePage === 'actions' && destinations.actions) {
              resumedPageRef.current = 'actions';
              goToPage('actions');
              return;
            }
            resumedPageRef.current = null;
            endResume();
            goToPage('landing');
            return;
          }

          discardResumeSnapshot();
          endResume();

          // From here on it's a fresh install or a credential update — an admin-only flow.
          // NOW verify the org actually has the MediaBriz product before provisioning
          // (skipped in dev when VITE_SKIP_PRODUCT_CHECK is set). Running it here (after the
          // already-installed short-circuit above) is what lets non-admin viewers open
          // Insights without the Integrations view permission.
          if (import.meta.env.VITE_SKIP_PRODUCT_CHECK !== 'true') {
            setLoadingKey('loading-validating');
            const product = await validateProductAvailability();
            if (!product.available && !product.permissionDenied) {
              showError('error-product-not-available', 'error-product-not-available-msg');
              return;
            }
            // A 403 means the caller lacks integrations:integration:view, which
            // that Genesys list requires. It is not the Mediabriz admin check.
            // CloudBriz Admin is enforced below. A successful call that does
            // not find the integration type is still "not purchased".
          }

          if (existingStatus.exists && !existingStatus.hasCredentials) {
            // Pre-provisioned org without credentials -- allow wizard in update mode
            setInstallMode('update');
            setOrgLoginLimitEnabled(existingStatus.loginLimitEnabled === true);
            setOrgAiActionsEnabled(existingStatus.aiActionsEnabled !== false);
            setConversationsEnabled(existingStatus.conversationsEnabled !== false);
            if (existingStatus.organization && existingStatus.region) {
              setExistingOrgInfo({
                organization: existingStatus.organization,
                region: existingStatus.region,
              });
            }
          }

          // Check install permissions (per Genesys reference getMissingInstallPermissions).
          // 'wizard' mode: checks admin permissions needed to create objects (role:add, oauth:client:add, etc.)
          // 'all' mode: also requires premiumAppViewPermission (admin must have assigned it first)
          // 'premium' mode: only requires premiumAppViewPermission
          // 'none' mode: skip all checks (Genesys default)
          if (wizardConfig.checkInstallPermissions && wizardConfig.checkInstallPermissions !== 'none') {
            const userPerms: string[] = userMe.authorization?.permissions || [];
            const allRequiredPerms: string[] = [];

            if (wizardConfig.checkInstallPermissions === 'premium' || wizardConfig.checkInstallPermissions === 'all') {
              allRequiredPerms.push(wizardConfig.premiumAppViewPermission);
            }

            if (wizardConfig.checkInstallPermissions === 'wizard' || wizardConfig.checkInstallPermissions === 'all') {
              const permSets = wizardConfig.installPermissions;
              for (const perms of Object.values(permSets)) {
                for (const p of perms) {
                  if (!allRequiredPerms.includes(p)) allRequiredPerms.push(p);
                }
              }
            }

            const missing = allRequiredPerms.filter((perm) => {
              return !userPerms.some((up: string) => up === perm || up.startsWith(perm));
            });

            if (missing.length > 0) {
              showError('error-missing-permissions-title', 'error-missing-permissions', undefined, missing);
              return;
            }
          }

          installEligibleRef.current = true;
          setCurrentStep(1);
          goToPage('welcome');
        };
        finishSetupRef.current = finishSetup;

        if (isEmbeddedInIframe()) {
          // Prefer silent restore so already-installed orgs open Landing (Settings /
          // Insights) without a Sign-in / install-wizard chrome. Only when the
          // persisted token is missing or dead do we show Sign in (popup needs a click).
          setLoadingKey('loading-authenticating');
          const restored = await tryRestoreGenesysSession(env);
          if (restored) {
            await finishSetup();
            return;
          }
          pendingFinishSetupRef.current = finishSetup;
          console.info('[AUTH] Embedded Apps iframe: waiting for Sign in click before PKCE popup');
          setPage('signin');
          return;
        }

        await finishSetup();
      } catch (e) {
        console.error('Setup error:', e);
        showSignInFailure(e);
      }
    }

    setup();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Expiry time and tab resume (a sleeping laptop stops timers) both end the session here.
  useEffect(() => {
    if (!signedIn) return;
    return watchSession({ checkAlive: checkGenesysTokenAlive });
  }, [signedIn]);

  useEffect(() => {
    if (resumedPageRef.current && page !== 'loading' && page !== resumedPageRef.current) {
      resumedPageRef.current = null;
      endResume();
    }
  }, [page]);

  const handleSignIn = useCallback(async () => {
    const finishSetup = pendingFinishSetupRef.current;
    if (!finishSetup) {
      window.location.reload();
      return;
    }
    setSigningIn(true);
    try {
      await finishSetup();
    } catch (e) {
      console.error('Sign-in error:', e);
      showSignInFailure(e);
    } finally {
      setSigningIn(false);
    }
  }, [showSignInFailure]);

  /**
   * Sign in again from the session-expired dialog. Keeps the page and its
   * unsaved edits (no secrets) in this tab, then runs the same sign-in: a
   * popup inside Genesys (this click is the gesture), a redirect at top level.
   */
  const handleReauth = useCallback(async () => {
    const resumePage: ResumePage | null = page === 'settings' || page === 'actions'
      ? page
      : page === 'landing' || page === 'complete' ? 'landing' : null;
    if (resumePage && orgId) saveResumeSnapshot(resumePage, orgId);
    clearGenesysToken();
    const finishSetup = finishSetupRef.current;
    if (!finishSetup) {
      window.location.reload();
      return;
    }
    setReauthBusy(true);
    setReauthErrorKey(null);
    try {
      await finishSetup();
    } catch (e) {
      // Still expired: the dialog stays over the error screen so the admin can try again.
      console.error('Sign-in again error:', e);
      setReauthErrorKey(SIGN_IN_FAILURE_KEYS[signInFailureKind(e)].message);
      showSignInFailure(e);
    } finally {
      setReauthBusy(false);
    }
  }, [page, orgId, showSignInFailure]);

  const handleInstall = useCallback(async () => {
    if (!installEligibleRef.current) {
      goToPage('provisioning');
      return;
    }
    // A token at or near its expiry would fail partway and leave a client behind.
    if (!usableAccessToken()) {
      reportSessionExpired();
      return;
    }
    setIsInstalling(true);
    goToPage('provisioning');

    const step2: ProgressType = installMode === 'update'
      ? { step: 'update-credentials', status: 'pending' }
      : { step: 'register-org', status: 'pending' };
    const initialSteps: ProgressType[] = [
      { step: 'oauth-client', status: 'pending' },
      step2,
    ];
    setProvisioningSteps(initialSteps);

    try {
      const orgRegistrationName = orgShortName || sanitizeOrgName(orgName);
      const result = await install(
        {
          organization: orgRegistrationName,
          region: gcEnvironment,
          dataRegion,
          genesysOrgId: orgId,
          scanEnabled: true,
          // New orgs start with Conversation Actions on. No type list is sent:
          // the toolbar writes the new-org aiActionsAllowedTypes on create, so
          // it decides which action types a new org may create.
          aiActionsEnabled: true,
          ...(gcLangTag ? { gcLangTag } : {}),
          ...(gcTargetEnv ? { gcTargetEnv } : {}),
        },
        (progress) => {
          setProvisioningSteps((prev) =>
            prev.map((s) => (s.step === progress.step ? progress : s)),
          );
        },
        installMode,
        existingOrgInfo,
      );

      if (result.status) {
        // Create always writes scanEnabled: true and aiActionsEnabled: true;
        // update keeps the existing flags (a pre-provisioned org can be Login
        // Limit only). Installers already passed CloudBriz Admin to reach this page.
        const destinations = landingDestinations({
          canManageSettings: true,
          loginLimitEnabled: orgLoginLimitEnabled,
          aiActionsEnabled: installMode === 'create' || orgAiActionsEnabled,
          conversationsEnabled,
        });
        setLoginLimitEnabled(orgLoginLimitEnabled);
        setShowSettingsRow(destinations.settings);
        setShowActionsMenu(destinations.actions);
        setCurrentStep(4);
        goToPage('complete');
      } else {
        const leftover = readLeftoverClient(orgId);
        if (leftover) {
          showLeftoverClient(leftover, result.cause);
          return;
        }
        showError(
          'error-post-custom-setup-failure',
          'error-post-custom-setup-msg',
          result.cause,
        );
      }
    } catch (e: unknown) {
      console.error('Installation error:', e);
      let msg: string;
      if (e instanceof Error) {
        msg = `${e.name}: ${e.message}`;
      } else if (typeof e === 'object' && e !== null) {
        const errObj = e as Record<string, unknown>;
        msg = errObj.message as string || errObj.body as string || JSON.stringify(e);
      } else {
        msg = String(e);
      }
      const leftover = readLeftoverClient(orgId);
      if (leftover) {
        showLeftoverClient(leftover, msg);
      } else if (isSessionExpired()) {
        // Nothing was left behind; the session-expired dialog offers Sign in again.
      } else if (e instanceof GenesysUnavailableError) {
        showError('error-installation', 'error-genesys-unavailable');
      } else {
        showError('error-installation', 'error-server-generic', msg);
      }
    } finally {
      setIsInstalling(false);
    }
  }, [orgName, orgShortName, gcEnvironment, dataRegion, orgId, gcLangTag, gcTargetEnv, installMode, existingOrgInfo, orgLoginLimitEnabled, orgAiActionsEnabled, conversationsEnabled, showError, showLeftoverClient, goToPage]);

  // Render
  const openSettings = (from: SettingsReturnPage) => {
    setSettingsReturnPage(from);
    goToPage('settings');
  };

  const continueToLanding = () => {
    if (DEV_PREVIEW_ENABLED && readDevWizardPreview()) {
      const url = new URL(window.location.href);
      url.searchParams.set('preview', 'landing');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
    goToPage('landing');
  };

  return (
    <div className={`wizard-container${page === 'actions' || page === 'settings' || isInstallChromePage(page) ? ' wizard-container--wide' : ''}`}>
      {page !== 'landing' && page !== 'settings' && page !== 'actions' && page !== 'signin' && (
        <div className="wizard-header">
          <img src={logo} alt="Mediabriz" className="wizard-header__logo" />
        </div>
      )}

      {isInstallChromePage(page) && (
        <StepIndicator
          currentStep={currentStep}
          steps={WIZARD_STEPS}
        />
      )}

      {page === 'loading' && <LoadingSpinner message={t(loadingKey)} />}

      {page === 'uninstalling' && <LoadingSpinner message={t(loadingKey)} />}

      {page === 'signin' && (
        <SignInPage onSignIn={handleSignIn} busy={signingIn} />
      )}

      {page === 'error' && (
        <ErrorPage
          title={t(errorTitleKey)}
          message={t(errorMessageKey)}
          details={errorDetails}
          missingPermissions={missingPermissions}
          isServiceUnavailable={isServiceUnavailable}
        />
      )}

      {page === 'welcome' && (
        <WelcomePage
          onNext={() => {
            setCurrentStep(2);
            goToPage('setup');
          }}
        />
      )}

      {page === 'setup' && (
        <SetupPage
          orgName={orgName}
          dataRegion={dataRegion}
          onDataRegionChange={setDataRegion}
          onNext={() => {
            setCurrentStep(3);
            goToPage('install-summary');
          }}
          onBack={() => {
            setCurrentStep(1);
            goToPage('welcome');
          }}
        />
      )}

      {page === 'install-summary' && (
        <InstallSummaryPage
          onInstall={handleInstall}
          onBack={() => {
            setCurrentStep(2);
            goToPage('setup');
          }}
          isInstalling={isInstalling}
          orgName={orgName}
          dataRegion={dataRegion}
        />
      )}

      {page === 'provisioning' && (
        <ProvisioningProgress steps={provisioningSteps} />
      )}

      {page === 'complete' && (
        <SetupCompletePage
          orgShortName={orgShortName}
          dataRegion={dataRegion}
          gcEnvironment={gcEnvironment}
          showSettings={showSettingsRow}
          showActions={showActionsMenu}
          conversationsEnabled={conversationsEnabled}
          onContinue={continueToLanding}
        />
      )}

      {page === 'landing' && (
        <LandingPage
          insightsUrl={insightsUrl}
          showSettings={showSettingsRow}
          showActions={showActionsMenu}
          showAdminNote={showAdminNote}
          conversationsEnabled={conversationsEnabled}
          onOpenSettings={() => openSettings('landing')}
          onOpenActions={() => goToPage('actions')}
        />
      )}

      {page === 'settings' && (
        <SettingsPage
          genesysOrgId={orgId}
          genesysRegion={gcEnvironment}
          loginLimitEnabled={loginLimitEnabled}
          conversationsEnabled={conversationsEnabled}
          onBack={() => goToPage(settingsReturnPage)}
          preview={preview?.page === 'settings'}
        />
      )}

      {page === 'actions' && (
        <ConversationActionsPage
          genesysOrgId={orgId}
          genesysRegion={gcEnvironment}
          insightsUrl={insightsUrl}
          onBack={() => goToPage('landing')}
          preview={preview?.page === 'actions'}
        />
      )}

      <ConfirmDialogHost />
      {signedIn && sessionExpired && page !== 'loading' && page !== 'signin' && page !== 'uninstalling' && (
        <SessionExpiredDialog
          onSignIn={handleReauth}
          busy={reauthBusy}
          error={reauthErrorKey ? t(reauthErrorKey) : null}
        />
      )}
    </div>
  );
}
